import { type FC, type ReactNode, type PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react'
import { dataLayerPublish } from '@dhl/datalayer'
import { type StepHandler, WizardContext, type WizardContextProps } from '~/setup/model/context.js'
import { convertChildrenToSteps, type InternalStepProps } from './Step.js'

type WrapperProps = {
  currentStep: ReactNode
}

type Props = {
  wrapper: (props: WrapperProps) => ReactNode
  onStepComplete?: (name: string, index: number) => Promise<void>
  onComplete?: () => void
}

const isStepActive = (step: { props: InternalStepProps }, wizardState: Record<string, string>): boolean => {
  const { branchKey, branchValue } = step.props

  if (!branchKey || !branchValue) {
    return true
  }

  return wizardState[branchKey] === branchValue
}

export const Wizard: FC<PropsWithChildren<Props>> = ({
  children,
  wrapper,
  onComplete,
  onStepComplete,
}: PropsWithChildren<Props>) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [title, setTitle] = useState('')
  const nextStepHandler = useRef<StepHandler>(() => {})
  const [currentStepCanContinue, setCurrentStepCanContinue] = useState(true)
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set())
  const submittedValuesRef = useRef<Record<string, unknown>>({})
  const [wizardState, setWizardStateInternal] = useState<Record<string, string>>({})
  const wizardStateRef = useRef(wizardState)
  wizardStateRef.current = wizardState
  const currentStepRef = useRef(currentStep)
  currentStepRef.current = currentStep

  // Only need to set this once, since the structure of the children does not change throughout the wizard.
  // Adding the children dependency causes functions that have a dependency on this value to change on every render,
  // which in some cases results in an infinite loop in consuming components.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allSteps = useMemo(() => convertChildrenToSteps(children), [])

  const findActiveStep = useCallback(
    (fromIndex: number, state: Record<string, string>, direction: 'forward' | 'back'): number => {
      const step = direction === 'forward' ? 1 : -1
      for (let i = fromIndex + step; i >= 0 && i < allSteps.length; i += step) {
        if (isStepActive(allSteps[i], state)) {
          return i
        }
      }
      return -1
    },
    [allSteps]
  )

  const currentChild = allSteps[currentStep]
  const { name: currentStepName, optional } = currentChild.props

  const resetStepToInitialState = useCallback(() => {
    nextStepHandler.current = null
    setCurrentStepCanContinue(true)
  }, [])

  const setWizardState = useCallback((key: string, value: string) => {
    const newState = { ...wizardStateRef.current, [key]: value }
    wizardStateRef.current = newState
    setWizardStateInternal(newState)
  }, [])

  const navigateToNextStep = useCallback(() => {
    const next = findActiveStep(currentStepRef.current, wizardStateRef.current, 'forward')

    if (next === -1) {
      return
    }

    setCurrentStep(next)
  }, [findActiveStep])

  const previousStep = useCallback(() => {
    if (currentStepRef.current <= 0) {
      return
    }
    resetStepToInitialState()

    const prev = findActiveStep(currentStepRef.current, wizardStateRef.current, 'back')
    if (prev === -1) {
      return
    }

    // If navigating back from a branch step to a non-branch step, clear the branch selection
    const currentBranchKey = allSteps[currentStepRef.current].props.branchKey
    const targetBranchKey = allSteps[prev].props.branchKey

    if (currentBranchKey && !targetBranchKey) {
      const { [currentBranchKey]: ignored, ...rest } = wizardStateRef.current
      wizardStateRef.current = rest
      setWizardStateInternal(rest)
    }

    setCurrentStep(prev)
  }, [allSteps, findActiveStep, resetStepToInitialState])

  const skipStep = useCallback(() => {
    const currentGroup = allSteps[currentStepRef.current].props.group
    const nextStepNum = allSteps.findIndex(
      (c, index) =>
        index > currentStepRef.current && c.props.group !== currentGroup && isStepActive(c, wizardStateRef.current)
    )

    if (nextStepNum === -1) {
      onComplete?.()
      return
    }

    resetStepToInitialState()
    setCurrentStep(nextStepNum)
  }, [allSteps, resetStepToInitialState, onComplete])

  const goToStep = useCallback(
    (stepName: string) => {
      resetStepToInitialState()

      const index = allSteps.findIndex((s) => s.props.name === stepName)
      setCurrentStep(index)
    },
    [allSteps, resetStepToInitialState]
  )

  const registerStepHandler = useCallback((handler: StepHandler) => {
    nextStepHandler.current = handler
  }, [])

  const setStepSubmittedValues = useCallback((stepName: string, values: unknown) => {
    submittedValuesRef.current[stepName] = values
    setCompletedSteps((prev) => new Set(prev).add(stepName))
  }, [])

  const getStepSubmittedValues = useCallback(
    <T,>(stepName: string) => submittedValuesRef.current[stepName] as T | undefined,
    []
  )

  const processNextStep = useCallback(async () => {
    const hasNext = findActiveStep(currentStepRef.current, wizardStateRef.current, 'forward') !== -1
    const goToNextStep = hasNext ? navigateToNextStep : onComplete

    if (nextStepHandler.current) {
      try {
        setIsLoading(true)
        await nextStepHandler.current()

        resetStepToInitialState()
        goToNextStep?.()
      } catch (error) {
        if ((error as Error).message !== 'validation failed') {
          dataLayerPublish('ERROR', { error, page: 'setup-wizard' })
        }
      }
      setIsLoading(false)
    } else {
      resetStepToInitialState()
      goToNextStep?.()
    }
  }, [findActiveStep, navigateToNextStep, onComplete, resetStepToInitialState])

  const handleStepComplete = useCallback(
    async () => onStepComplete?.(currentStepName, currentStep),
    [currentStep, currentStepName, onStepComplete]
  )

  const currentStepContextValue = useMemo(
    () => ({
      isOptional: !!optional,
      canContinue: currentStepCanContinue,
      stepNumber: currentStep,
      stepName: currentStepName,
      hasPreviousStep: findActiveStep(currentStep, wizardState, 'back') !== -1,
      isPreviouslyCompleted: completedSteps.has(currentStepName),
    }),
    [completedSteps, currentStep, currentStepCanContinue, currentStepName, findActiveStep, optional, wizardState]
  )

  const metaValue = useMemo(
    () => ({
      title,
      isLoading,
      steps: allSteps.map((s) => s.props.name),
      activeSteps: allSteps.filter((s) => isStepActive(s, wizardState)).map((s) => s.props.name),
    }),
    [allSteps, isLoading, title, wizardState]
  )

  const contextValue = useMemo<WizardContextProps>(
    () => ({
      nextStep: processNextStep,
      previousStep,
      registerStepHandler,
      skipStep,
      goToStep,
      setCurrentStepCanContinue,
      onStepComplete: handleStepComplete,
      setIsLoading,
      setTitle,
      setStepSubmittedValues,
      getStepSubmittedValues,
      setWizardState,
      wizardState,
      currentStep: currentStepContextValue,
      meta: metaValue,
    }),
    [
      processNextStep,
      previousStep,
      registerStepHandler,
      skipStep,
      goToStep,
      setCurrentStepCanContinue,
      handleStepComplete,
      setTitle,
      setStepSubmittedValues,
      getStepSubmittedValues,
      setWizardState,
      wizardState,
      currentStepContextValue,
      metaValue,
    ]
  )

  return <WizardContext.Provider value={contextValue}>{wrapper({ currentStep: currentChild })}</WizardContext.Provider>
}
